import { db, storage } from '@/data/firebase/config';
import { GalleryImage } from '@/domain/types/collections';
import { doc, updateDoc } from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from 'firebase/storage';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FiTrash2, FiUpload } from 'react-icons/fi';
import { MdImage } from 'react-icons/md';
import { toast } from 'react-toastify';

interface GalleryImagesUploadProps {
  images: GalleryImage[];
  onChange: (images: GalleryImage[]) => void;
  galleryId?: string; // Si existe, permite actualizar directamente en Firestore
  collectionName?: string;
}

const GalleryImagesUpload: React.FC<GalleryImagesUploadProps> = ({
  images,
  onChange,
  galleryId,
  collectionName = 'galleries',
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [editingCaption, setEditingCaption] = useState<number | null>(null);

  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('No se pudo crear el contexto del canvas'));
            return;
          }

          // Mantener dimensiones originales
          canvas.width = img.width;
          canvas.height = img.height;

          // Dibujar imagen sin modificar relación de aspecto
          ctx.drawImage(img, 0, 0, img.width, img.height);

          // Convertir a blob con compresión (quality 0.8 para reducir peso)
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Error al comprimir la imagen'));
                return;
              }
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            0.8, // Calidad de compresión (0-1)
          );
        };
        img.onerror = () => reject(new Error('Error al cargar la imagen'));
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
    });
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setUploading(true);
      const newImages: GalleryImage[] = [];

      try {
        for (let i = 0; i < acceptedFiles.length; i++) {
          const file = acceptedFiles[i];

          // Comprimir la imagen antes de subirla
          toast.info(`Comprimiendo imagen ${i + 1}/${acceptedFiles.length}...`);
          const compressedFile = await compressImage(file);

          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = ref(storage, `galleries/${fileName}`);

          const uploadTask = uploadBytesResumable(storageRef, compressedFile);

          await new Promise<void>((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                const progress =
                  ((i + snapshot.bytesTransferred / snapshot.totalBytes) /
                    acceptedFiles.length) *
                  100;
                setUploadProgress(Math.round(progress));
              },
              (error) => {
                console.error('Upload error:', error);
                reject(error);
              },
              async () => {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref,
                );
                newImages.push({
                  url: downloadURL,
                  caption: '',
                  captionEN: '',
                });
                resolve();
              },
            );
          });
        }

        const updatedImages = [...images, ...newImages];
        onChange(updatedImages);

        // Si hay un galleryId, actualizar también en Firestore
        if (galleryId) {
          const docRef = doc(db, collectionName, galleryId);
          await updateDoc(docRef, { images: updatedImages });
        }

        toast.success(
          `${newImages.length} imagen(es) subida(s) correctamente`,
          {
            theme: 'colored',
          },
        );
      } catch (error) {
        console.error('Error uploading images:', error);
        toast.error('Error al subir las imágenes', { theme: 'colored' });
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [images, onChange, galleryId, collectionName],
  );

  const handleRemoveImage = async (index: number) => {
    try {
      const imageToRemove = images[index];

      // Intentar eliminar del storage
      if (imageToRemove.url.includes('firebase')) {
        try {
          const imageRef = ref(storage, imageToRemove.url);
          await deleteObject(imageRef);
        } catch (e) {
          console.warn('No se pudo eliminar la imagen del storage:', e);
        }
      }

      const updatedImages = images.filter((_, i) => i !== index);
      onChange(updatedImages);

      // Si hay un galleryId, actualizar también en Firestore
      if (galleryId) {
        const docRef = doc(db, collectionName, galleryId);
        await updateDoc(docRef, { images: updatedImages });
      }

      toast.success('Imagen eliminada correctamente', { theme: 'colored' });
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error('Error al eliminar la imagen', { theme: 'colored' });
    }
  };

  const handleCaptionChange = (
    index: number,
    field: 'caption' | 'captionEN',
    value: string,
  ) => {
    const updatedImages = images.map((img, i) =>
      i === index ? { ...img, [field]: value } : img,
    );
    onChange(updatedImages);
  };

  const handleCaptionSave = async () => {
    setEditingCaption(null);
    if (galleryId) {
      const docRef = doc(db, collectionName, galleryId);
      await updateDoc(docRef, { images });
      toast.success('Descripción actualizada', { theme: 'colored' });
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    },
    disabled: uploading,
  });

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <FiUpload className="mx-auto text-4xl text-gray-400 mb-2" />
        {uploading ? (
          <div>
            <p className="text-gray-600">Subiendo imágenes...</p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-1">{uploadProgress}%</p>
          </div>
        ) : isDragActive ? (
          <p className="text-blue-600">Suelta las imágenes aquí...</p>
        ) : (
          <div>
            <p className="text-gray-600">
              Arrastra y suelta imágenes aquí, o haz clic para seleccionar
            </p>
            <p className="text-sm text-gray-400 mt-1">
              PNG, JPG, GIF, WEBP (múltiples archivos permitidos)
            </p>
          </div>
        )}
      </div>

      {/* Grid de imágenes */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="relative group border rounded-lg overflow-hidden bg-white shadow-sm"
            >
              {/* Imagen */}
              <div className="aspect-square relative">
                <img
                  src={image.url}
                  alt={image.caption || `Imagen ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {/* Botón eliminar */}
                <button
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  title="Eliminar imagen"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>

              {/* Captions */}
              <div className="p-2 space-y-2">
                {editingCaption === index ? (
                  <>
                    <input
                      type="text"
                      placeholder="Descripción (ES)"
                      value={image.caption || ''}
                      onChange={(e) =>
                        handleCaptionChange(index, 'caption', e.target.value)
                      }
                      className="w-full text-sm border rounded px-2 py-1"
                    />
                    <input
                      type="text"
                      placeholder="Caption (EN)"
                      value={image.captionEN || ''}
                      onChange={(e) =>
                        handleCaptionChange(index, 'captionEN', e.target.value)
                      }
                      className="w-full text-sm border rounded px-2 py-1"
                    />
                    <button
                      onClick={handleCaptionSave}
                      className="w-full text-sm bg-blue-500 text-white rounded py-1 hover:bg-blue-600"
                    >
                      Guardar
                    </button>
                  </>
                ) : (
                  <div
                    onClick={() => setEditingCaption(index)}
                    className="cursor-pointer hover:bg-gray-50 rounded p-1"
                  >
                    <p className="text-sm text-gray-700 truncate">
                      {image.caption || (
                        <span className="text-gray-400 italic">
                          Sin descripción (ES)
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {image.captionEN || (
                        <span className="text-gray-400 italic">
                          No caption (EN)
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Estado vacío */}
      {images.length === 0 && !uploading && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <MdImage className="mx-auto text-5xl text-gray-300 mb-2" />
          <p className="text-gray-500">No hay imágenes en esta galería</p>
        </div>
      )}
    </div>
  );
};

export default GalleryImagesUpload;
